import type { IComponentSchema, IFormilySchema } from '../../types';

/**
 * A typed sub-collection of a node: rows of `field` (e.g. `manufacturedItems`)
 * rendered as cards inside their owner node, with their own add/edit/remove
 * actions. Rows are leaves — only packages recurse.
 */
export interface IChildCollection {
  key: string;
  field: string;
  label?: string;
  modalTitle?: string;
  canEdit?: boolean;
  canDelete?: boolean;
  style?: Record<string, any>;
  formSchema?: IFormilySchema;
  cardSchema?: IComponentSchema<any> | null;
}

/** Sub-schemas may arrive stringified (see `escapeFormItemsSchemas`). */
export const parseSubSchema = (value: any) => {
  if (!value) return value;
  const parsed = typeof value === 'string' ? JSON.parse(value) : value;
  const childSchemaForm = parsed?.properties?.childSchemaForm;
  if (typeof childSchemaForm !== 'string') return parsed;
  return {
    ...parsed,
    properties: {
      ...parsed.properties,
      childSchemaForm: JSON.parse(childSchemaForm),
    },
  };
};

export interface ITreeNode {
  item: Record<string, any>;
  children: ITreeNode[];
  /** Set only on sub-collection rows; absent on package nodes. */
  collection?: IChildCollection;
  /** Id of the node owning the row this node was built from. */
  ownerId?: string;
}

const collectionNodes = (
  item: Record<string, any>,
  collections: IChildCollection[]
): ITreeNode[] =>
  collections.flatMap(collection => {
    const rows = item[collection.field];
    if (!Array.isArray(rows)) return [];
    return rows
      .filter(row => !row?.toBeDeleted)
      .map(row => ({
        item: row,
        children: [],
        collection,
        ownerId: item.id as string,
      }));
  });

export const buildTree = (
  items: Record<string, any>[],
  collections: IChildCollection[] = []
): ITreeNode[] => {
  const map = new Map<string, ITreeNode>();
  const roots: ITreeNode[] = [];

  items.forEach(item => {
    map.set(item.id as string, { item, children: [] });
  });

  items.forEach(item => {
    const node = map.get(item.id as string)!;
    if (item?.parent?.id == null) {
      roots.push(node);
    } else {
      const parent = map.get(item?.parent?.id as string);
      if (parent) {
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    }
  });

  if (collections.length) {
    map.forEach(node => {
      node.children = [
        ...node.children,
        ...collectionNodes(node.item, collections),
      ];
    });
  }

  return roots;
};

/**
 * Deep-removes array items flagged `toBeDeleted: true` at every level, returning
 * a clean copy. DISPLAY-ONLY — use it for rendering (e.g. a node's custom
 * component), never on the field value, since the tombstoned items must stay in
 * the value so the deletion reaches the server.
 */
export const stripToBeDeletedDeep = <T>(value: T): T => {
  if (Array.isArray(value)) {
    return value
      .filter(
        item =>
          !(
            item &&
            typeof item === 'object' &&
            (item as any).toBeDeleted === true
          )
      )
      .map(stripToBeDeletedDeep) as unknown as T;
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>)) {
      out[key] = stripToBeDeletedDeep((value as Record<string, unknown>)[key]);
    }
    return out as unknown as T;
  }
  return value;
};

export const getDescendantIds = (
  items: Record<string, any>[],
  rootId: string
): Set<string> => {
  const result = new Set<string>([rootId]);
  const queue = [rootId];

  while (queue.length > 0) {
    const current = queue.shift()!;
    items.forEach(item => {
      if (item?.parent?.id === current) {
        const id = item.id as string;
        result.add(id);
        queue.push(id);
      }
    });
  }

  return result;
};
